import {
  Button,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Row,
  Select,
  Textarea,
} from '@/components'
import { TInput } from '@/components/forms/form-elements/types'
import { FlexWrapper } from '@/components/layout/flex/flexWrapper'
import { useAxios } from '@/context/axiosContext'
import { useDialog } from '@/context/dialogContext'
import { useRedis } from '@/hooks/useRedis'
import commonUtils from '@/utils/commonUtils'
import React, { useEffect, useRef, useState } from 'react'
import DataTable, { TableColumn } from 'react-data-table-component'
import { useLocation, useNavigate } from 'react-router-dom'

interface OptionData {
  surveySn: string
  optionSn: string
  optionTitle: string
  optionDesc: string
  useYn: string
  delYn: string
  count: number
  regId: string
  regDate: string
  updId: string
  updDate: string
}

interface CommentData {
  cstId: string
  commentSn: string
  commentCn: string
  surveySn: string
  optionSn: string
  parentCommentSn: string
  accessIp: string
  useYn: string
  delYn: string
  adminDelYn: string

  userId: string
  name: string
  nickName: string
  withDrawYn: string

  optionTitle: string
  optionDesc: string

  regId: string
  regDate: string
  updId: string
  updDate: string
}

const DetailComSurvey: React.FC = () => {
  const axios = useAxios()
  const navigate = useNavigate()
  const location = useLocation()

  const { getComCodes, getComCodeValue } = useRedis()

  const { showAlert, showConfirm } = useDialog()

  const [masterData, setMasterData] = useState({
    surveySn: '',
    surveyTitle: '',
    surveyDesc: '',
    surveyStartDate: '',
    surveyEndDate: '',
    isAnonymous: 'N',
    isMultiple: 'N',
    maxChoices: '',
    useYn: 'Y',
    delYn: 'N',
    count: -1,
    regId: '',
    regDate: '',
    updId: '',
    updDate: '',
  })

  const [options, setOptions] = useState<OptionData[]>([])
  const [comments, setComments] = useState<CommentData[]>([])

  const [selectOption, setSelectOption] = useState('')

  const optionsRef = useRef<HTMLDivElement>(null)
  const [showOptions, setShowOptions] = useState(true)

  const commentsRef = useRef<HTMLDivElement>(null)
  const [showComments, setShowComments] = useState(false)

  const handleButtonDeleteClick = (commentSn: string) => {
    showConfirm({
      title: '해당 댓글을 삭제할까요?',
      async onConfirm() {
        const response: any = await axios.post('/survey/deleteComment', {
          commentSn: commentSn,
          adminDelYn: 'Y',
        })
        if (response.status === 200 && !response.data.common.resultCode) {
          getSurvey()
        } else {
          showAlert({
            title: response.data.common.resultMsg,
          })
        }
      },
    })
  }

  const columns: TableColumn<CommentData>[] = [
    {
      name: '옵션제목',
      width: '15%',
      center: true,
      cell: (row) => (
        <div
          style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          title={row.optionTitle}
        >
          {row.optionTitle}
        </div>
      ),
    },
    {
      name: '닉네임',
      width: '10%',
      center: true,
      selector: (row) => row.nickName,
    },
    {
      name: '내용',
      cell: (row) => (
        <div
          style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          title={row.commentCn}
        >
          {row.parentCommentSn != null && row.parentCommentSn != ''
            ? '↳ ' + row.commentCn
            : row.commentCn}
        </div>
      ),
      width: '500px',
    },
    {
      name: '삭제여부',
      selector: (row) =>
        getComCodeValue('DEL_YN', row.delYn)?.codeNm || row.delYn,
      width: '8%',
    },
    {
      name: '', // 관리자 삭제기능
      center: true,
      cell: (row) => (
        <>
          {row.adminDelYn == 'Y' || row.delYn == 'Y' ? (
            <>삭제됨</>
          ) : (
            <Button
              variant="outlined"
              color="neverbe"
              size="xs"
              onClick={() => handleButtonDeleteClick(row.commentSn)}
            >
              삭제
            </Button>
          )}
        </>
      ),
      sortable: false,
      width: '10%',
    },
    {
      name: '등록자',
      selector: (row) => row.regId,
      center: true,
      wrap: true,
      width: '8%',
    },
    {
      name: '등록일시',
      selector: (row) => commonUtils.formatDate(row.regDate, true),
      center: true,
      wrap: true,
      width: '15%',
    },
    {
      name: '수정자',
      selector: (row) => row.updId,
      center: true,
      wrap: true,
      width: '8%',
    },
    {
      name: '수정일시',
      selector: (row) => commonUtils.formatDate(row.updDate, true),
      center: true,
      wrap: true,
      width: '15%',
    },
  ]

  const handleInputChange = (e: React.ChangeEvent<TInput>) => {
    const name = e.target.name
    let value = e.target.value

    if (name == 'surveyDesc' && value.length > 255) {
      return
    }

    if (name == 'surveyStartDate' || name == 'surveyEndDate') {
      value = e.target.value.replace(/[^0-9]/g, '')
    }

    setMasterData((prevState) => {
      return { ...prevState, [name]: value }
    })
  }

  const handleOptionChange = (e: React.ChangeEvent<TInput>) => {
    // const name = e.target.name
    // const id = e.target.id
    // const value = e.target.value
    // const idx = Number(id.replace(name, ''))
    // setOptions((prevItems) => {
    //   const newOptions = [...prevItems]
    //   newOptions[idx] = {
    //     ...newOptions[idx],
    //     [name]: value,
    //   }
    //   return newOptions
    // })

    const name = e.target.name
    const id = e.target.id
    const value = e.target.value
    const idx = Number(id.replace(name, ''))

    // maxLength 체크 - 문자열 필드에만 적용
    const maxLengths: Record<string, number> = {
      optionTitle: 20,
      optionDesc: 150,
    }

    const maxLength = maxLengths[name]
    if (maxLength) {
      const currentValue = options[idx]?.[name as keyof (typeof options)[0]]
      const currentLength =
        typeof currentValue === 'string' ? currentValue.length : 0

      if (value.length > maxLength && value.length > currentLength) {
        showAlert({
          type: 'warning',
          title: `최대 ` + `${maxLength}` + `자까지만 입력할 수 있어요.`,
        })

        return // maxLength를 초과하고 현재보다 길어지는 경우만 막음
      }
    }

    setOptions((prevItems) => {
      const newOptions = [...prevItems]
      newOptions[idx] = {
        ...newOptions[idx],
        [name]: value,
      }
      return newOptions
    })
  }

  const insertOption = (idx: number) => {
    if (options.length - 1 < idx || idx < 0) return

    showConfirm({
      title: '옵션을 추가할까요?',
      async onConfirm() {
        let optionItem = ''
        let optionItemNm = ''
        let optionIdx = -1

        if (options[idx]) {
          if (options[idx].optionTitle == '') {
            optionItem = 'optionTitle'
            optionItemNm = '옵션제목'
            optionIdx = idx
          } else if (options[idx].optionDesc == '') {
            optionItem = 'optionDesc'
            optionItemNm = '옵션설명'
            optionIdx = idx
          }
        }
        if (optionItem != '') {
          showAlert({
            type: 'warning',
            title: optionItemNm + '을 확인하세요',
            onClose() {
              document.getElementById(optionItem + optionIdx)?.focus()
            },
          })
          return
        }

        const response: any = await axios.post(
          '/survey/insertOption',
          options[idx],
        )
        if (response.status === 200 && !response.data.common.resultCode) {
          // TODO 성공코드값
          getSurvey()
        } else {
          showAlert({
            title: '옵션추가를 실패했어요.',
          })
        }
      },
    })
  }

  const updateOption = (idx: number) => {
    if (options.length - 1 < idx || idx < 0) return

    showConfirm({
      title: '옵션을 수정할까요?',
      async onConfirm() {
        let optionItem = ''
        let optionItemNm = ''
        let optionIdx = -1

        if (options[idx]) {
          if (options[idx].optionTitle == '') {
            optionItem = 'optionTitle'
            optionItemNm = '옵션제목'
            optionIdx = idx
          } else if (options[idx].optionDesc == '') {
            optionItem = 'optionDesc'
            optionItemNm = '옵션설명'
            optionIdx = idx
          }
        }
        if (optionItem != '') {
          showAlert({
            type: 'warning',
            title: optionItemNm + '을 확인하세요',
            onClose() {
              document.getElementById(optionItem + optionIdx)?.focus()
            },
          })
          return
        }

        const response: any = await axios.post(
          '/survey/updateOption',
          options[idx],
        )
        if (response.status === 200 && !response.data.common.resultCode) {
          // TODO 성공코드값
          getSurvey()
        } else {
          showAlert({
            title: '옵션변경을 실패했어요.',
          })
        }
      },
    })
  }

  const updateSurvey = async () => {
    // 마스터 데이터 검증
    if (masterData.surveyTitle == '') {
      showAlert({
        type: 'warning',
        title: '투표제목을 입력하세요',
        onClose() {
          document.getElementById('surveyTitle')?.focus()
        },
      })
      return
    }
    if (masterData.surveyDesc == '') {
      showAlert({
        type: 'warning',
        title: '투표설명을 입력하세요',
        onClose() {
          document.getElementById('surveyDesc')?.focus()
        },
      })
      return
    }
    if (masterData.surveyStartDate == '') {
      showAlert({
        type: 'warning',
        title: '투표시작일을 입력하세요',
        onClose() {
          document.getElementById('surveyStartDate')?.focus()
        },
      })
      return
    }
    if (masterData.surveyEndDate == '') {
      showAlert({
        type: 'warning',
        title: '투표종료일을 입력하세요',
        onClose() {
          document.getElementById('surveyEndDate')?.focus()
        },
      })
      return
    }

    if (!commonUtils.isYYYYMMDD(masterData.surveyStartDate)) {
      showAlert({
        type: 'warning',
        title: '투표시작일을 확인하세요',
        onClose() {
          document.getElementById('surveyStartDate')?.focus()
        },
      })
      return
    }
    if (!commonUtils.isYYYYMMDD(masterData.surveyEndDate)) {
      showAlert({
        type: 'warning',
        title: '투표종료일을 확인하세요',
        onClose() {
          document.getElementById('surveyEndDate')?.focus()
        },
      })
      return
    }

    if (Number(masterData.surveyEndDate) < Number(masterData.surveyStartDate)) {
      showAlert({
        type: 'warning',
        title: '투표종료일이 투표시작일보다 과거일 수 없습니다.',
        onClose() {
          document.getElementById('surveyEndDate')?.focus()
        },
      })
      return
    }

    const response: any = await axios.post('/survey/update', masterData)
    if (response.status === 200 && !response.data.common.resultCode) {
      // TODO 성공코드값
      navigate('/survey')
    } else {
      showAlert({
        title: response.data.common.resultMsg,
      })
    }
  }

  const getSurvey = async () => {
    const response: any = await axios.post('/survey/detail', {
      surveySn: location.state.surveySn,
    })
    if (response.status === 200 && !response.data.common.resultCode) {
      const master = response.data.data.master
      const options = response.data.data.options
      const comments = response.data.data.comments
      master.surveyStartDate = master.surveyStartDate
        ?.split(' ')[0]
        .replace(/-/g, '')
      master.surveyEndDate = master.surveyEndDate
        ?.split(' ')[0]
        .replace(/-/g, '')

      setMasterData(master)
      setOptions(options)
      setComments(comments)
    } else {
      showAlert({
        title: '잘못된 접근입니다.',
        onClose() {
          navigate('/survey', {
            state: { search: location.state?.search || '' },
          })
        },
      })
    }
  }

  useEffect(() => {
    getSurvey()
  }, [])

  return (
    <>
      <div style={{ width: '100%', height: '100%', marginTop: '50px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            marginBottom: '10px',
            alignItems: 'center',
            marginTop: '50px',
          }}
        >
          <p style={{ fontSize: '20px' }}>투표 관리</p>
          <FlexWrapper gap="20px">
            <Button
              onClick={() =>
                showConfirm({
                  title: '투표목록 화면으로 이동할까요?',
                  onConfirm() {
                    navigate('/survey', {
                      state: { search: location.state?.search || '' },
                    })
                  },
                })
              }
              color="neverbe"
              variant="outlined"
            >
              목록
            </Button>
            <Button
              onClick={() =>
                showConfirm({
                  title: '투표를 수정할까요?',
                  onConfirm() {
                    updateSurvey()
                  },
                })
              }
              color="neverbe"
            >
              저장
            </Button>
            <Button
              onClick={() => setShowComments(!showComments)}
              color="neverbe"
              variant="outlined"
            >
              댓글관리
            </Button>
          </FlexWrapper>
        </div>
      </div>
      <div>
        <Row>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="surveyTitle">
                투표제목&nbsp;
                <span style={{ fontSize: '13px', color: 'red' }}>
                  * 60자까지 입력가능
                </span>
              </Label>
              <div style={{ marginBottom: '5px' }} />
              <Input
                id="surveyTitle"
                name="surveyTitle"
                type="text"
                maxLength={60}
                value={masterData.surveyTitle}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="surveyDesc">
                투표설명&nbsp;
                <span style={{ fontSize: '13px', color: 'red' }}>
                  * 255자까지 입력가능
                </span>
              </Label>
              <div style={{ marginBottom: '5px' }} />
              <Textarea
                id="surveyDesc"
                name="surveyDesc"
                value={masterData.surveyDesc}
                height={'200px'}
                maxLength={255}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="surveyStartDate">
                투표시작일
              </Label>
              <div style={{ marginBottom: '5px' }} />
              <Input
                id="surveyStartDate"
                name="surveyStartDate"
                type="text"
                placeholder="YYYYMMDD"
                maxLength={8}
                value={masterData.surveyStartDate}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="surveyEndDate">
                투표종료일
              </Label>
              <div style={{ marginBottom: '5px' }} />
              <Input
                id="surveyEndDate"
                name="surveyEndDate"
                type="text"
                placeholder="YYYYMMDD"
                maxLength={8}
                value={masterData.surveyEndDate}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="isAnonymous">
                익명여부
              </Label>
              <div style={{ marginBottom: '5px' }} />
              <Select
                id="isAnonymous"
                name="isAnonymous"
                value={masterData.isAnonymous}
                onChange={handleInputChange}
              >
                {getComCodes('IS_ANONYMOUS').map((item) => (
                  <option key={'isAnonymous' + item.code} value={item.code}>
                    {item.codeNm}
                  </option>
                ))}
              </Select>
            </FormGroup>
          </Col>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="isMultiple">
                다중선택여부
              </Label>
              <div style={{ marginBottom: '5px' }} />
              <Select
                id="isMultiple"
                name="isMultiple"
                value={masterData.isMultiple}
                onChange={handleInputChange}
              >
                {getComCodes('IS_MULTIPLE').map((item) => (
                  <option key={'isMultiple' + item.code} value={item.code}>
                    {item.codeNm}
                  </option>
                ))}
              </Select>
            </FormGroup>
          </Col>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="maxChoices">
                다중선택개수
              </Label>
              <div style={{ marginBottom: '5px' }} />
              <Input
                type="number"
                name="maxChoices"
                id="maxChoices"
                value={masterData.maxChoices}
                onChange={handleInputChange}
                disabled={masterData.isMultiple == 'N'}
                max={options.length}
                min={0}
              />
            </FormGroup>
          </Col>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="useYn">
                사용여부
              </Label>
              <div style={{ marginBottom: '5px' }} />
              <Select
                id="useYn"
                name="useYn"
                value={masterData.useYn}
                onChange={handleInputChange}
              >
                {getComCodes('USE_YN').map((item) => (
                  <option key={'useYn' + item.code} value={item.code}>
                    {item.codeNm}
                  </option>
                ))}
              </Select>
            </FormGroup>
          </Col>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="delYn">
                삭제여부
              </Label>
              <div style={{ marginBottom: '5px' }} />
              <Select
                id="delYn"
                name="delYn"
                value={masterData.delYn}
                onChange={handleInputChange}
              >
                {getComCodes('DEL_YN').map((item) => (
                  <option key={'delYn' + item.code} value={item.code}>
                    {item.codeNm}
                  </option>
                ))}
              </Select>
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col md={2}>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor={`count`}>
                투표수
              </Label>
              <div style={{ marginBottom: '5px' }} />
              <Input
                id={`count`}
                name={`count`}
                type="text"
                value={masterData.count}
                disabled={true}
              />
            </FormGroup>
          </Col>
          <Col md={2}>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor={`regId`}>
                등록자
              </Label>
              <div style={{ marginBottom: '5px' }} />
              <Input
                id={`regId`}
                name={`regId`}
                type="text"
                value={masterData.regId}
                disabled={true}
              />
            </FormGroup>
          </Col>
          <Col md={3}>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor={`regDate`}>
                등록일시
              </Label>
              <div style={{ marginBottom: '5px' }} />
              <Input
                id={`regDate`}
                name={`regDate`}
                value={commonUtils.formatDate(masterData.regDate, true)}
                disabled={true}
              />
            </FormGroup>
          </Col>
          <Col md={2}>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor={`updId`}>
                수정자
              </Label>
              <div style={{ marginBottom: '5px' }} />
              <Input
                id={`updId`}
                name={`updId`}
                type="text"
                value={masterData.updId}
                disabled={true}
              />
            </FormGroup>
          </Col>
          <Col md={3}>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor={`updDate`}>
                수정일시
              </Label>
              <div style={{ marginBottom: '5px' }} />
              <Input
                id={`updDate`}
                name={`updDate`}
                value={commonUtils.formatDate(masterData.updDate, true)}
                disabled={true}
              />
            </FormGroup>
          </Col>
        </Row>
        <div
          style={{
            display: 'flex',
            justifyContent: 'right',
            width: '100%',
            marginBottom: '10px',
            alignItems: 'center',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
              marginBottom: '10px',
              alignItems: 'center',
            }}
          >
            <p style={{ fontSize: '20px' }}>옵션 등록</p>
            <FlexWrapper gap="20px">
              {/* <Button
                onClick={() => {
                  setShowOptions(!showOptions);
                }}
                color="neverbe"
                variant="texted"
              >
                {showOptions ? '접기' : '펼치기'}
              </Button> */}
              <Button
                onClick={() => {
                  setShowOptions(true)
                  if (optionsRef != null) {
                    setTimeout(() => {
                      optionsRef.current?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'end',
                      })
                      if (optionsRef.current) {
                        optionsRef.current.scrollTop =
                          optionsRef.current.scrollHeight
                      }
                    }, 300)
                  }
                  setOptions([
                    ...options,
                    {
                      surveySn: masterData.surveySn,
                      optionSn: '',
                      optionTitle: '',
                      optionDesc: '',
                      useYn: 'Y',
                      delYn: 'N',
                      count: -1,
                      regId: '',
                      regDate: '',
                      updId: '',
                      updDate: '',
                    },
                  ])
                }}
                color="neverbe"
              >
                추가
              </Button>
            </FlexWrapper>
          </div>
        </div>
        <div
          ref={optionsRef}
          style={{
            transition: 'max-height 0.3s ease-in-out, opacity 0.3s ease-in-out',
            maxHeight: showOptions
              ? `${optionsRef.current?.scrollHeight}px`
              : '0px',
            opacity: showOptions ? 1 : 0,
            overflowX: 'hidden',
            overflowY: 'auto',
          }}
        >
          {options.map((option, idx) => {
            return (
              <React.Fragment key={`option` + idx}>
                <Row>
                  <Col md={8}>
                    <FormGroup mb="20px">
                      <Label
                        display="block"
                        mb="5px"
                        htmlFor={`optionTitle` + idx}
                      >
                        옵션제목&nbsp;
                        <span style={{ fontSize: '13px', color: 'red' }}>
                          * 8자까지 입력가능
                        </span>
                      </Label>
                      <div style={{ marginBottom: '5px' }} />
                      <Input
                        id={`optionTitle` + idx}
                        name={`optionTitle`}
                        type="text"
                        maxLength={8}
                        value={option.optionTitle}
                        onChange={handleOptionChange}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup mb="20px">
                      <Label display="block" mb="5px" htmlFor={`useYn` + idx}>
                        사용여부
                      </Label>
                      <div style={{ marginBottom: '5px' }} />
                      <Select
                        id={`useYn` + idx}
                        name={`useYn`}
                        value={option.useYn}
                        onChange={handleOptionChange}
                      >
                        {getComCodes('USE_YN').map((item) => (
                          <option key={'useYn' + item.code} value={item.code}>
                            {item.codeNm}
                          </option>
                        ))}
                      </Select>
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup mb="20px">
                      <Label display="block" mb="5px" htmlFor={`delYn` + idx}>
                        삭제여부
                      </Label>
                      <div style={{ marginBottom: '5px' }} />
                      <Select
                        id={`delYn` + idx}
                        name={`delYn`}
                        value={option.delYn}
                        onChange={handleOptionChange}
                      >
                        {getComCodes('DEL_YN').map((item) => (
                          <option key={'delYn' + item.code} value={item.code}>
                            {item.codeNm}
                          </option>
                        ))}
                      </Select>
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md={10}>
                    <FormGroup mb="20px">
                      <Label
                        display="block"
                        mb="5px"
                        htmlFor={`optionDesc` + idx}
                      >
                        옵션설명&nbsp;
                        <span style={{ fontSize: '13px', color: 'red' }}>
                          * 150자까지 입력가능
                        </span>
                      </Label>
                      <div style={{ marginBottom: '5px' }} />
                      {/* <Input
                          id={`optionDesc` + idx}
                          name={`optionDesc`}
                          type="text"
                          maxLength={255}
                          value={option.optionDesc}
                          onChange={handleOptionChange}
                        // disabled={isMsgCdDisabled}
                        /> */}
                      <Textarea
                        id={`optionDesc` + idx}
                        name={`optionDesc`}
                        value={option.optionDesc}
                        onChange={handleOptionChange}
                        maxLength={150}
                        height={'200px'}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup mb="20px">
                      <Label htmlFor={''}>
                        <></>
                      </Label>
                      <FlexWrapper gap="10px">
                        <Button
                          onClick={() => {
                            // deleteOption(idx);
                            if (option.count < 0) {
                              insertOption(idx)
                            } else {
                              updateOption(idx)
                            }
                          }}
                          color="neverbe"
                          variant="outlined"
                        >
                          {option.count < 0 ? '추가' : '저장'}
                        </Button>
                      </FlexWrapper>
                    </FormGroup>
                  </Col>
                </Row>
                {option.count >= 0 && (
                  <Row>
                    <Col md={2}>
                      <FormGroup mb="20px">
                        <Label display="block" mb="5px" htmlFor={`count` + idx}>
                          투표수
                        </Label>
                        <div style={{ marginBottom: '5px' }} />
                        <Input
                          id={`count` + idx}
                          name={`count`}
                          type="text"
                          value={option.count}
                          disabled={true}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={2}>
                      <FormGroup mb="20px">
                        <Label display="block" mb="5px" htmlFor={`regId` + idx}>
                          등록자
                        </Label>
                        <div style={{ marginBottom: '5px' }} />
                        <Input
                          id={`regId` + idx}
                          name={`regId`}
                          type="text"
                          value={option.regId}
                          disabled={true}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup mb="20px">
                        <Label
                          display="block"
                          mb="5px"
                          htmlFor={`regDate` + idx}
                        >
                          등록일시
                        </Label>
                        <div style={{ marginBottom: '5px' }} />
                        <Input
                          id={`regDate` + idx}
                          name={`regDate`}
                          value={commonUtils.formatDate(option.regDate, true)}
                          disabled={true}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={2}>
                      <FormGroup mb="20px">
                        <Label display="block" mb="5px" htmlFor={`updId` + idx}>
                          수정자
                        </Label>
                        <div style={{ marginBottom: '5px' }} />
                        <Input
                          id={`updId` + idx}
                          name={`updId`}
                          type="text"
                          value={option.updId}
                          disabled={true}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup mb="20px">
                        <Label
                          display="block"
                          mb="5px"
                          htmlFor={`updDate` + idx}
                        >
                          수정일시
                        </Label>
                        <div style={{ marginBottom: '5px' }} />
                        <Input
                          id={`updDate` + idx}
                          name={`updDate`}
                          value={commonUtils.formatDate(option.updDate, true)}
                          disabled={true}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                )}
                <hr style={{ marginBottom: '15px' }} />
              </React.Fragment>
            )
          })}
        </div>
        {showComments && (
          <div
            style={{
              position: 'absolute' /* 화면에 고정 */,
              top: '30%',
              width: '70%' /* 너비 조정 */,
              padding: '10px',
              zIndex: '5' /* 다른 요소 위에 표시 */,
              background: 'white',
              border: '1px solid',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'right',
                width: '100%',
                marginBottom: '10px',
                alignItems: 'center',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  width: '100%',
                  marginBottom: '10px',
                  alignItems: 'center',
                }}
              >
                <p style={{ fontSize: '20px', marginLeft: '20px' }}>
                  댓글 관리
                </p>
                <FlexWrapper gap="20px">
                  <FormGroup>
                    <Select
                      id=""
                      name=""
                      value={selectOption}
                      onChange={(e) => {
                        setSelectOption(e.target.value)
                      }}
                      width={'100px'}
                    >
                      <option value="">전체</option>
                      {options.map((option) =>
                        option.optionSn == null ||
                        option.optionSn == '' ? null : (
                          <option
                            value={option.optionSn}
                            title={option.optionTitle}
                            key={'option' + option.optionSn}
                          >
                            {option.optionTitle?.substring(0, 5)}
                          </option>
                        ),
                      )}
                    </Select>
                  </FormGroup>
                  <Button
                    variant="texted"
                    color="neverbe"
                    size="xs"
                    onClick={() => {
                      setShowComments(false)
                    }}
                    margin={'10px'}
                  >
                    X
                  </Button>
                </FlexWrapper>
              </div>
            </div>
            <div
              ref={commentsRef}
              style={{
                overflowX: 'auto',
                overflowY: 'hidden',
                maxWidth: '100%',
              }}
            >
              <DataTable
                columns={columns} // 컬럼정보(header)
                data={comments.filter(
                  (comment) =>
                    selectOption == '' || comment.optionSn == selectOption,
                )} // 화면에 표시될 데이터
                pagination={true} // 페이징 여부
                paginationRowsPerPageOptions={[10, 20, 30, 50]} // 한화면에 보여줄 데이터 셀렉트박스
                sortIcon={<></>} // 정렬아이콘 공백처리
                noDataComponent={<>조회결과가 없습니다.</>}
                paginationPerPage={10}
                customStyles={{
                  headRow: {
                    style: {
                      maxWidth: '100%', // 원하는 너비 설정
                    },
                  },
                  rows: {
                    style: {
                      maxWidth: '100%', // 원하는 너비 설정
                    },
                  },
                }}
              />
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default DetailComSurvey
